import React from "react"
import {
	Grid,
	Typography,
	IconButton
} from "@material-ui/core"
import clsx from "clsx"
import {
	MessageCircle as MessageCircleIcon,
	Shield as ShieldIcon,
	Gift as GiftIcon,
	MoreHorizontal as MoreHorizontalIcon,
	Plus as PlusIcon
} from "lucide-react"

import { useReferAndEarn } from "@/hooks/useReferAndEarn"
import { useMobileNav } from "@/contexts/MobileNavContext"
import { usePermissionStateStore } from "@/store/PermissionState"

import { InboxInnerType } from "@/pages/Attendance/Sidebar"
import MoreSheet from "@/pages/Attendance/MobileBottomTabBar/MoreSheet"
import ManagerPanelGroupPermissionGate from "@/components/PermissionGate/ManagerPanelGroupPermissionGate"
import useStyles from "@/pages/Attendance/MobileBottomTabBar/styles"

type MobileBottomTabBarProps = {
	activeSection: InboxInnerType
	isMoreSheetOpen: boolean
	isUserSettingsOpen: boolean
	onInboxClick: () => void
	onAdminClick: () => void
	onMoreClick: () => void
	onCloseMoreSheet: () => void
	onUserSettingsOpen: () => void
}

const FAB_ICON_SIZE_IN_PIXELS = 26
const MORE_ICON_STROKE_WIDTH = 3
const TAB_ICON_SIZE_IN_PIXELS = 22

const MobileBottomTabBar: React.FC<MobileBottomTabBarProps> = (props) => {
	const {
		activeSection,
		isMoreSheetOpen,
		isUserSettingsOpen,
		onInboxClick,
		onAdminClick,
		onMoreClick,
		onCloseMoreSheet,
		onUserSettingsOpen
	} = props

	const classes = useStyles()
	const { openReferAndEarnPage } = useReferAndEarn()
	const { newChatDrawerOpenerRef } = useMobileNav()
	const { checkPermission } = usePermissionStateStore()

	const isInboxActive = activeSection === "inbox" && !isUserSettingsOpen
	const isAdminActive = activeSection === "admin"
	const isMoreActive = isMoreSheetOpen || isUserSettingsOpen
	const canAccessManagerPanel = checkPermission("manager-panel", "access")

	const handleIndiqueClick = () => {
		openReferAndEarnPage()
	}

	const handleAdminButtonClick = () => {
		if (!canAccessManagerPanel) {
			return
		}

		onAdminClick()
	}

	const handleFabClick = () => {
		newChatDrawerOpenerRef.current?.()
	}

	return (
		<>
			<Grid
				container
				alignItems="flex-end"
				className={classes.tabBar}
			>
				<Grid
					item
					xs
					container
					direction="column"
					alignItems="center"
					className={classes.tabItem}
				>
					<Grid item>
						<IconButton
							disableRipple
							onClick={onInboxClick}
							className={clsx(classes.tabIconInactive, isInboxActive && classes.tabIconActive)}
						>
							<MessageCircleIcon size={TAB_ICON_SIZE_IN_PIXELS} />
						</IconButton>
					</Grid>
					<Grid item>
						<Typography
							className={clsx(classes.tabLabel, classes.tabLabelInactive, isInboxActive && classes.tabLabelActive)}
						>
							Inbox
						</Typography>
					</Grid>
				</Grid>

				<Grid
					item
					xs
					container
					direction="column"
					alignItems="center"
					className={classes.tabItem}
				>
					<ManagerPanelGroupPermissionGate action="access">
						<Grid
							container
							direction="column"
							alignItems="center"
						>
							<Grid item>
								<IconButton
									disableRipple
									onClick={handleAdminButtonClick}
									className={clsx(classes.tabIconInactive, isAdminActive && classes.tabIconActive)}
								>
									<ShieldIcon size={TAB_ICON_SIZE_IN_PIXELS} />
								</IconButton>
							</Grid>
							<Grid item>
								<Typography
									className={clsx(classes.tabLabel, classes.tabLabelInactive, isAdminActive && classes.tabLabelActive)}
								>
									Gestor
								</Typography>
							</Grid>
						</Grid>
					</ManagerPanelGroupPermissionGate>
				</Grid>

				<Grid
					item
					xs
					container
					justifyContent="center"
					alignItems="flex-end"
					className={classes.fabContainer}
				>
					<Grid item>
						<IconButton
							disableRipple
							onClick={handleFabClick}
							className={classes.newChatFabButton}
						>
							<PlusIcon
								size={FAB_ICON_SIZE_IN_PIXELS}
								strokeWidth={2.4}
							/>
						</IconButton>
					</Grid>
				</Grid>

				<Grid
					item
					xs
					container
					direction="column"
					alignItems="center"
					className={classes.tabItem}
				>
					<Grid item>
						<IconButton
							disableRipple
							onClick={handleIndiqueClick}
							className={classes.tabIconInactive}
						>
							<GiftIcon size={TAB_ICON_SIZE_IN_PIXELS} />
						</IconButton>
					</Grid>
					<Grid item>
						<Typography
							className={clsx(classes.tabLabel, classes.tabLabelInactive)}
						>
							Indique
						</Typography>
					</Grid>
				</Grid>

				<Grid
					item
					xs
					container
					direction="column"
					alignItems="center"
					className={classes.tabItem}
				>
					<Grid item>
						<IconButton
							disableRipple
							onClick={onMoreClick}
							className={clsx(classes.tabIconInactive, isMoreActive && classes.tabIconActive)}
						>
							<MoreHorizontalIcon
								size={TAB_ICON_SIZE_IN_PIXELS}
								strokeWidth={MORE_ICON_STROKE_WIDTH}
							/>
						</IconButton>
					</Grid>
					<Grid item>
						<Typography
							className={clsx(classes.tabLabel, classes.tabLabelInactive, isMoreActive && classes.tabLabelActive)}
						>
							Mais
						</Typography>
					</Grid>
				</Grid>
			</Grid>

			<MoreSheet
				open={isMoreSheetOpen}
				onClose={onCloseMoreSheet}
				onUserSettingsOpen={onUserSettingsOpen}
				onReferAndEarnClick={openReferAndEarnPage}
			/>
		</>
	)
}

export default MobileBottomTabBar
